import type { ProfileId } from "../../content/types";

export interface RouteCatalogIngredient {
  name: string;
  display: string;
}

export interface RouteCatalogRecipe {
  id: string;
  title: string;
  description: string;
  category: string;
  mealType: string[];
  timeMinutes: number;
  timeLabel: string;
  servings: number;
  yieldLabel: string;
  difficulty: string;
  image: string;
  ingredients: RouteCatalogIngredient[];
  instructions: string[];
  tags: string[];
  equipment: string[];
  restrictions: string[];
  costLevel: string;
  proteinLevel: string;
  childFriendly: boolean;
  mealPrepFriendly: boolean;
  makeAhead: boolean;
  naturalCookingTip: string;
  substitutions: string;
  storageTips: string;
  accompaniment: string;
  collections: string[];
}

export interface RouteRecipeCatalog {
  schemaVersion: 1;
  source: string;
  sourceRecipeCount: number;
  culinaryRecipeCount: number;
  catalogDigest: string;
  recipes: RouteCatalogRecipe[];
}

export interface PersonalizedRouteRecipe extends RouteCatalogRecipe {
  imageUrl: string;
  why: string;
  servingMultiplier: number;
  servingNote: string;
}

export interface PersonalizedWeeklyPlanItem {
  day: number;
  dayLabel: string;
  recipeId: string;
  recipeTitle: string;
  mealLabel: string;
  prepNote: string;
}

export interface PersonalizedShoppingItem {
  id: string;
  name: string;
  quantities: string[];
  recipeIds: string[];
}

export interface PersonalizedShoppingSection {
  id: "hortifruti" | "proteinas" | "mercearia" | "temperos";
  label: string;
  items: PersonalizedShoppingItem[];
}

export interface PersonalizedRecipeRoute {
  schemaVersion: 1;
  catalogDigest: string;
  catalogRecipeCount: number;
  profileId: ProfileId;
  headline: string;
  preparationStyle: { id: string; label: string; description: string };
  equipment: { ids: string[]; label: string };
  household: { people: number; label: string };
  shoppingPlan: { id: string; label: string; tip: string };
  recipes: PersonalizedRouteRecipe[];
  weeklyPlan: PersonalizedWeeklyPlanItem[];
  shoppingList: PersonalizedShoppingSection[];
}

export type NormalizedRouteAnswers = Partial<Record<string, readonly string[]>>;

interface ScoredRecipe {
  recipe: RouteCatalogRecipe;
  score: number;
  reasons: string[];
}

const APP_RECIPE_ORIGIN = "https://app.transformesuasaude.com.br";
const DAY_LABELS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"] as const;

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("pt-BR")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function values(answers: NormalizedRouteAnswers, questionId: string): string[] {
  return [...(answers[questionId] ?? [])];
}

function firstValue(answers: NormalizedRouteAnswers, questionId: string, fallback = ""): string {
  return values(answers, questionId)[0] ?? fallback;
}

function combinedRecipeText(recipe: RouteCatalogRecipe): string {
  return normalize([
    recipe.title,
    recipe.description,
    recipe.category,
    ...recipe.mealType,
    ...recipe.tags,
    ...recipe.collections,
    ...recipe.ingredients.flatMap((ingredient) => [ingredient.name, ingredient.display]),
  ].join(" "));
}

function ingredientText(recipe: RouteCatalogRecipe): string {
  return normalize(recipe.ingredients.flatMap((ingredient) => [ingredient.name, ingredient.display]).join(" "));
}

function includesAny(text: string, needles: readonly string[]): boolean {
  return needles.some((needle) => text.includes(normalize(needle)));
}

function hasRestriction(recipe: RouteCatalogRecipe, restriction: string): boolean {
  const target = normalize(restriction);
  return recipe.restrictions.some((item) => normalize(item) === target);
}

function requiredEquipment(recipe: RouteCatalogRecipe): Set<string> {
  const required = new Set<string>();
  for (const item of recipe.equipment) {
    const equipment = normalize(item);
    if (!equipment || equipment.includes("opcional")) continue;
    if (includesAny(equipment, ["airfryer", "air fryer"])) required.add("airfryer");
    else if (equipment.includes("forno")) required.add("forno");
    else if (includesAny(equipment, ["liquidificador", "processador", "mixer"])) required.add("liquidificador_processador");
    else if (includesAny(equipment, ["fogao", "panela", "frigideira", "cuscuzeira", "cozimento a vapor"])) required.add("fogao_panelas");
  }
  return required;
}

function equipmentCompatible(recipe: RouteCatalogRecipe, answers: NormalizedRouteAnswers): boolean {
  const selected = new Set(values(answers, "q13"));
  const required = requiredEquipment(recipe);
  if (selected.has("utensilios_basicos")) return required.size === 0;
  return [...required].every((equipment) => selected.has(equipment));
}

function dietaryCompatible(recipe: RouteCatalogRecipe, answers: NormalizedRouteAnswers): boolean {
  const considerations = new Set(values(answers, "q7"));
  const diet = firstValue(answers, "q8");
  const ingredients = ingredientText(recipe)
    .replaceAll("leite de coco", "")
    .replaceAll("leite vegetal", "")
    .replaceAll("manteiga de amendoim", "")
    .replaceAll("manteiga de coco", "");
  const meatWords = ["carne", "frango", "porco", "suino", "bovina", "peixe", "atum", "sardinha", "salmao", "tilapia", "camarao", "peru"];

  if (considerations.has("sem_leite") && includesAny(ingredients, ["leite", "queijo", "iogurte", "manteiga", "requeijao", "nata", "creme de leite"])) return false;
  if (considerations.has("sem_gluten") && !hasRestriction(recipe, "Sem glúten") && includesAny(ingredients, ["trigo", "centeio", "cevada", "malte", "farinha de trigo", "pao", "macarrao"])) return false;
  if (considerations.has("sem_ovos") && includesAny(ingredients, ["ovo", "ovos", "gema", "clara de ovo"])) return false;
  if (considerations.has("sem_carnes") && includesAny(ingredients, meatWords)) return false;
  if (considerations.has("sem_acucar_refinado") && includesAny(ingredients, ["acucar refinado", "acucar cristal", "acucar branco"])) return false;
  if (diet === "vegano" && !hasRestriction(recipe, "Vegano")) return false;
  if (diet === "vegetariano" && includesAny(ingredients, meatWords)) return false;
  return true;
}

function timeLimit(answers: NormalizedRouteAnswers): number | undefined {
  const answer = firstValue(answers, "q3");
  if (answer === "ate_15_minutos") return 15;
  if (answer === "20_a_30_minutos") return 30;
  if (answer === "30_a_45_minutos") return 45;
  return undefined;
}

function mealMatches(recipe: RouteCatalogRecipe, answer: string): boolean {
  const mealTypes = recipe.mealType.map(normalize);
  if (answer === "cafe_da_manha") return mealTypes.includes("cafe da manha");
  if (answer === "almoco") return mealTypes.some((item) => item === "almoco" || item === "marmita");
  if (answer === "jantar") return mealTypes.some((item) => item === "jantar" || item === "caldo" || item === "almoco");
  if (answer === "lanches") return mealTypes.some((item) => item === "lanche" || item === "cafe da manha");
  if (answer === "fim_de_semana") return recipe.timeMinutes >= 20 || mealTypes.includes("sobremesa") || mealTypes.includes("fermentado");
  return false;
}

function strictMealMatches(recipe: RouteCatalogRecipe, answer: string): boolean {
  const mealTypes = recipe.mealType.map(normalize);
  if (answer === "cafe_da_manha") return mealTypes.includes("cafe da manha");
  if (answer === "almoco") return mealTypes.some((item) => item === "almoco" || item === "marmita");
  if (answer === "jantar") return mealTypes.some((item) => item === "jantar" || item === "caldo");
  if (answer === "lanches") return mealTypes.includes("lanche");
  return answer === "fim_de_semana";
}

function categoryMatches(recipe: RouteCatalogRecipe, answer: string): boolean {
  const meals = recipe.mealType.map(normalize);
  if (answer === "refeicoes_rapidas") return recipe.timeMinutes <= 20;
  if (answer === "marmitas") return meals.includes("marmita") || recipe.mealPrepFriendly || recipe.makeAhead;
  if (answer === "cafes_e_lanches") return meals.some((item) => item === "cafe da manha" || item === "lanche");
  if (answer === "sopas_sucos_chas") return meals.some((item) => item === "caldo" || item === "bebida");
  if (answer === "doces_naturais") return meals.includes("sobremesa");
  if (answer === "fermentados_e_bases") return meals.includes("fermentado") || combinedRecipeText(recipe).includes("base");
  return false;
}

const FLAVOR_WORDS: Record<string, readonly string[]> = {
  caseiro: ["arroz", "feijao", "lentilha", "abobora", "sopa", "caldo", "assado", "canela", "bolo"],
  fresco: ["salada", "folhas", "limao", "hortela", "pepino", "suco", "smoothie", "fresco"],
  temperado: ["alho", "cebola", "gengibre", "paprica", "curry", "pimenta", "ervas", "temperado"],
  doce: ["banana", "cacau", "fruta", "doce", "sobremesa", "canela", "coco"],
  fermentado: ["ferment", "kefir", "picles", "chucrute", "kombucha", "probiot"],
};

function preparationStyle(answers: NormalizedRouteAnswers) {
  const id = firstValue(answers, "q14", "preparo_na_hora");
  const styles = {
    preparo_na_hora: {
      id,
      label: "Porções frescas ao longo da semana",
      description: "Receitas diretas, organizadas para preparar e servir no mesmo dia.",
    },
    cozinhar_e_repetir: {
      id,
      label: "Cozinhar uma vez e repetir com praticidade",
      description: "Preparos que rendem bem e ajudam a reduzir decisões nos dias seguintes.",
    },
    congelar_porcoes: {
      id,
      label: "Porções preparadas para congelar",
      description: "A rota prioriza receitas com bom potencial de preparo antecipado e armazenamento.",
    },
    bases_versateis: {
      id,
      label: "Bases versáteis para combinar",
      description: "Preparos que podem virar acompanhamentos, recheios ou novas combinações durante a semana.",
    },
  } as const;
  return styles[id as keyof typeof styles] ?? styles.preparo_na_hora;
}

function household(answers: NormalizedRouteAnswers) {
  const id = firstValue(answers, "q9", "duas_pessoas");
  if (id === "uma_pessoa") return { people: 1, label: "1 pessoa" };
  if (id === "tres_ou_quatro") return { people: 4, label: "3 ou 4 pessoas" };
  if (id === "cinco_ou_mais") return { people: 6, label: "5 pessoas ou mais" };
  return { people: 2, label: "2 pessoas" };
}

function shoppingPlan(answers: NormalizedRouteAnswers) {
  const id = firstValue(answers, "q10", "planeja_semana");
  const plans = {
    planeja_semana: { id, label: "Uma compra para a semana", tip: "Revise a lista inteira antes da sua compra semanal e marque o que já tem em casa." },
    alguns_dias: { id, label: "Compras em dois blocos", tip: "Compre primeiro os itens dos dias 1 a 4; deixe os alimentos mais frescos dos dias 5 a 7 para a segunda compra." },
    conforme_falta: { id, label: "Lista por reposição", tip: "Comece pelos itens usados em mais de uma receita e reponha os frescos conforme forem faltando." },
    decide_na_hora: { id, label: "Lista essencial primeiro", tip: "Garanta os ingredientes mais repetidos da rota; eles mantêm opções abertas mesmo quando você decide na hora." },
  } as const;
  return plans[id as keyof typeof plans] ?? plans.planeja_semana;
}

function equipmentLabel(answers: NormalizedRouteAnswers): { ids: string[]; label: string } {
  const ids = values(answers, "q13");
  if (ids.includes("utensilios_basicos")) return { ids, label: "Utensílios básicos" };
  const labels: Record<string, string> = {
    fogao_panelas: "fogão e panelas",
    forno: "forno",
    airfryer: "air fryer",
    liquidificador_processador: "liquidificador/processador",
  };
  return { ids, label: ids.map((id) => labels[id] ?? id).join(" + ") };
}

function scoreRecipe(recipe: RouteCatalogRecipe, answers: NormalizedRouteAnswers): ScoredRecipe {
  let score = 0;
  const reasons: string[] = [];
  const text = combinedRecipeText(recipe);
  const selectedMeal = firstValue(answers, "q2");
  if (mealMatches(recipe, selectedMeal)) {
    score += 12;
    reasons.push(`combina com seu foco em ${selectedMeal === "cafe_da_manha" ? "café da manhã" : selectedMeal.replaceAll("_", " ")}`);
  }

  const maximumTime = timeLimit(answers);
  if (maximumTime !== undefined) {
    if (recipe.timeMinutes > 0 && recipe.timeMinutes <= maximumTime) {
      score += 10;
      reasons.push(`cabe no seu limite de até ${maximumTime} minutos`);
    } else if (recipe.timeMinutes > maximumTime) score -= Math.min(12, Math.ceil((recipe.timeMinutes - maximumTime) / 5) * 2);
  }

  for (const category of values(answers, "q5")) if (categoryMatches(recipe, category)) score += 6;
  for (const flavor of values(answers, "q6")) if (includesAny(text, FLAVOR_WORDS[flavor] ?? [])) score += 3;

  const goal = firstValue(answers, "q1");
  if (goal === "organizar_semana" && (recipe.mealPrepFriendly || recipe.makeAhead || recipe.mealType.map(normalize).includes("marmita"))) score += 7;
  if (goal === "refeicoes_rapidas" && recipe.timeMinutes <= 20) score += 7;
  if (goal === "mais_variedade" && recipe.tags.length >= 3) score += 3;
  if (goal === "mais_leveza" && includesAny(text, ["salada", "sopa", "caldo", "legumes", "folhas", "suco"])) score += 5;
  if (goal === "cuidar_da_casa" && (recipe.childFriendly || recipe.servings >= 3)) score += 6;
  if (goal === "explorar_natural" && includesAny(text, ["ferment", "kefir", "brotos", "germin", "cru", "vivo"])) score += 6;

  const experience = firstValue(answers, "q4");
  const difficulty = normalize(recipe.difficulty);
  if ((experience === "iniciante" || experience === "basico") && difficulty.includes("facil")) score += 5;
  if (experience === "iniciante" && (difficulty.includes("dificil") || recipe.instructions.length > 8)) score -= 6;
  if (experience === "explorador" && (difficulty.includes("media") || includesAny(text, ["ferment", "tecnica", "germin"]))) score += 4;

  const diet = firstValue(answers, "q8");
  if (diet === "flexivel" && (hasRestriction(recipe, "Vegano") || hasRestriction(recipe, "Vegetariano"))) score += 2;
  const targetPeople = household(answers).people;
  score += Math.max(0, 4 - Math.abs(recipe.servings - targetPeople));

  const blocker = firstValue(answers, "q11");
  if (blocker === "falta_planejamento" && (recipe.mealPrepFriendly || recipe.makeAhead)) score += 5;
  if (blocker === "falta_tempo" && recipe.timeMinutes <= 20) score += 6;
  if (blocker === "falta_variedade" && recipe.tags.length >= 3) score += 3;
  if (blocker === "opcoes_pesadas" && includesAny(text, ["salada", "caldo", "sopa", "legumes", "folhas"])) score += 5;
  if (blocker === "agradar_casa" && recipe.childFriendly) score += 5;
  if (blocker === "informacao_solta" && recipe.instructions.length >= 4 && recipe.naturalCookingTip) score += 4;

  const resource = firstValue(answers, "q12");
  if (resource === "lista_de_compras" && recipe.ingredients.length >= 4) score += 2;
  if (resource === "filtro_rapidas" && recipe.timeMinutes <= 20) score += 4;
  if (resource === "busca_ingredientes" && recipe.ingredients.length >= 5) score += 2;
  if (resource === "colecoes_leves" && includesAny(text, ["salada", "caldo", "sopa", "legumes", "folhas"])) score += 3;
  if (resource === "favoritos" && recipe.childFriendly) score += 3;
  if (resource === "caderno" && (recipe.naturalCookingTip || recipe.substitutions)) score += 3;

  const prep = firstValue(answers, "q14");
  const storage = normalize(recipe.storageTips);
  const explicitlyFreezable = includesAny(storage, ["freezer", "congel"]) && !includesAny(storage, ["nao congel", "evite congel"]);
  if (prep === "preparo_na_hora") {
    if (recipe.timeMinutes <= 30 && !recipe.makeAhead) score += 22;
    if (includesAny(storage, ["sirva na hora", "consuma no mesmo dia"])) score += 8;
    if (recipe.makeAhead) score -= 5;
  }
  if (prep === "cozinhar_e_repetir") {
    if (recipe.servings >= 4) score += 24;
    if (recipe.makeAhead || recipe.mealPrepFriendly) score += 14;
  }
  if (prep === "congelar_porcoes") {
    if (explicitlyFreezable) score += 36;
    else if (recipe.makeAhead || recipe.mealPrepFriendly) score += 7;
    if (includesAny(storage, ["nao congel", "evite congel"])) score -= 28;
  }
  if (prep === "bases_versateis") {
    if (includesAny(text, ["base", "molho", "pasta", "recheio", "marmita"])) score += 28;
    if (recipe.mealPrepFriendly || recipe.makeAhead) score += 10;
  }
  if (requiredEquipment(recipe).size === 0) reasons.push("usa apenas utensílios simples");
  else reasons.push("usa os equipamentos que você marcou");
  if (prep !== "preparo_na_hora" && (recipe.mealPrepFriendly || recipe.makeAhead || storage)) reasons.push("funciona bem com a organização que você escolheu");

  return { recipe, score, reasons };
}

function selectRecipes(catalog: RouteRecipeCatalog, answers: NormalizedRouteAnswers): ScoredRecipe[] {
  const compatible = catalog.recipes.filter((recipe) => dietaryCompatible(recipe, answers) && equipmentCompatible(recipe, answers));
  if (compatible.length < 7) throw new Error("personalized_route_insufficient_compatible_recipes");
  const selectedMeal = firstValue(answers, "q2");
  const focused = compatible.filter((recipe) => strictMealMatches(recipe, selectedMeal));
  const maximumTime = timeLimit(answers);
  const withinTime = maximumTime === undefined
    ? compatible
    : compatible.filter((recipe) => recipe.timeMinutes > 0 && recipe.timeMinutes <= maximumTime);
  const focusedWithinTime = withinTime.filter((recipe) => strictMealMatches(recipe, selectedMeal));
  const candidates = focusedWithinTime.length >= 7
    ? focusedWithinTime
    : focused.length >= 7
      ? focused
      : withinTime.length >= 7
        ? withinTime
        : compatible;
  const sorted = candidates.map((recipe) => scoreRecipe(recipe, answers)).sort((left, right) =>
    right.score - left.score || left.recipe.timeMinutes - right.recipe.timeMinutes || left.recipe.id.localeCompare(right.recipe.id)
  );
  const selected: ScoredRecipe[] = [];
  const categoryCounts = new Map<string, number>();
  const familyCounts = new Map<string, number>();
  let beverages = 0;
  let desserts = 0;
  const focus = new Set(values(answers, "q5"));
  const recipeFamily = (recipe: RouteCatalogRecipe) => {
    const title = normalize(recipe.title);
    return ["marmita", "bowl", "salada", "sopa", "caldo", "pate", "molho", "suco", "vitamina", "bolo", "mousse", "creme", "pao"]
      .find((family) => title.split(" ").includes(family)) ?? "outros";
  };
  const tooSimilar = (candidate: RouteCatalogRecipe) => {
    const ignored = new Set(["a", "as", "com", "da", "das", "de", "do", "dos", "e", "em", "natural", "naturais", "rapido", "rapida", "simples"]);
    const tokens = new Set(normalize(candidate.title).split(" ").filter((token) => token.length > 2 && !ignored.has(token)));
    return selected.some(({ recipe }) => {
      const existing = new Set(normalize(recipe.title).split(" ").filter((token) => token.length > 2 && !ignored.has(token)));
      const smallest = Math.min(tokens.size, existing.size);
      if (smallest === 0) return false;
      const overlap = [...tokens].filter((token) => existing.has(token)).length;
      return overlap / smallest >= 0.75;
    });
  };

  for (const candidate of sorted) {
    if (selected.length === 7) break;
    const category = normalize(candidate.recipe.category);
    const meals = candidate.recipe.mealType.map(normalize);
    const isBeverage = meals.includes("bebida");
    const isDessert = meals.includes("sobremesa");
    const family = recipeFamily(candidate.recipe);
    const categoryLimit = 3;
    if ((categoryCounts.get(category) ?? 0) >= categoryLimit) continue;
    if (isBeverage && beverages >= (focus.has("sopas_sucos_chas") ? 2 : 1)) continue;
    if (isDessert && desserts >= (focus.has("doces_naturais") ? 2 : 1)) continue;
    if (family !== "outros" && (familyCounts.get(family) ?? 0) >= 2) continue;
    if (tooSimilar(candidate.recipe)) continue;
    selected.push(candidate);
    categoryCounts.set(category, (categoryCounts.get(category) ?? 0) + 1);
    familyCounts.set(family, (familyCounts.get(family) ?? 0) + 1);
    if (isBeverage) beverages += 1;
    if (isDessert) desserts += 1;
  }
  for (const candidate of sorted) {
    if (selected.length === 7) break;
    if (!selected.some((item) => item.recipe.id === candidate.recipe.id)) selected.push(candidate);
  }
  return selected;
}

function multiplierLabel(multiplier: number): string {
  if (multiplier === 0.5) return "½×";
  if (Number.isInteger(multiplier)) return `${multiplier}×`;
  return `${String(multiplier).replace(".", ",")}×`;
}

function servingAdjustment(recipe: RouteCatalogRecipe, people: number) {
  const raw = people / Math.max(1, recipe.servings);
  const multiplier = Math.max(0.5, Math.min(6, Math.round(raw * 2) / 2));
  if (multiplier === 1) return { multiplier, note: `${recipe.yieldLabel || `${recipe.servings} porções`} já combina com sua casa.` };
  if (multiplier < 1) return { multiplier, note: `Faça meia receita ou guarde uma porção para outro dia.` };
  return { multiplier, note: `Use cerca de ${multiplierLabel(multiplier)} as medidas para atender ${people} pessoas.` };
}

function recipeImageUrl(image: string): string {
  if (/^https:\/\//i.test(image)) return image;
  return `${APP_RECIPE_ORIGIN}${image.startsWith("/") ? image : `/${image}`}`;
}

function planNote(styleId: string, recipe: PersonalizedRouteRecipe, index: number): string {
  if (styleId === "cozinhar_e_repetir") {
    return index % 2 === 0
      ? "Prepare uma porção maior e reserve parte para reduzir o trabalho do próximo dia."
      : "Use como variação da semana; se a rotina apertar, aproveite a porção já reservada.";
  }
  if (styleId === "congelar_porcoes") {
    return recipe.storageTips && includesAny(normalize(recipe.storageTips), ["freezer", "congel"])
      ? "Prepare, deixe esfriar e siga a orientação de armazenamento da receita antes de congelar."
      : "Prepare no dia e congele apenas se os ingredientes e a textura permitirem.";
  }
  if (styleId === "bases_versateis") {
    return index === 0 || recipe.mealPrepFriendly || recipe.makeAhead
      ? "Prepare como base da semana e reserve em recipiente fechado."
      : "Combine com uma base já pronta para diminuir o tempo de montagem.";
  }
  return "Separe os ingredientes antes e prepare esta receita no próprio dia.";
}

const SHOPPING_SECTIONS = [
  { id: "hortifruti", label: "Hortifruti" },
  { id: "proteinas", label: "Proteínas e geladeira" },
  { id: "mercearia", label: "Grãos, sementes e mercearia" },
  { id: "temperos", label: "Temperos e outros" },
] as const;

function shoppingSection(value: string): PersonalizedShoppingSection["id"] {
  const text = normalize(value);
  if (includesAny(text, ["frango", "carne", "peixe", "atum", "sardinha", "salmao", "ovo", "iogurte", "queijo", "leite", "tofu"])) return "proteinas";
  if (includesAny(text, ["arroz", "feijao", "lentilha", "grao de bico", "quinoa", "aveia", "farinha", "castanha", "amendoim", "semente", "cacau", "coco", "massa", "pao"])) return "mercearia";
  if (includesAny(text, ["sal", "azeite", "oleo", "vinagre", "canela", "paprica", "pimenta", "cominho", "curry", "acucar", "fermento", "molho"])) return "temperos";
  return "hortifruti";
}

function shoppingItemName(ingredient: RouteCatalogIngredient): string {
  const display = ingredient.display.trim();
  if (!display) return ingredient.name.trim();
  const withoutAmount = display
    .replace(/^\s*(?:\d+(?:[.,/]\d+)?|[¼½¾])(?:\s+de\b)?\s*/u, "")
    .replace(/^\s*(?:kg|g|mg|l|ml|litros?(?:\s+e\s+meio)?)\s+(?:de\s+)?/iu, "")
    .replace(/^\s*(?:xícaras?|colher(?:es)?\s+de\s+(?:sopa|chá)|colher(?:es)?|latas?|unidades?|dentes?|fatias?|folhas?|ramos?|pitadas?)\s+(?:de\s+)?/iu, "")
    .trim();
  return withoutAmount || ingredient.name.trim() || display;
}

function shoppingList(recipes: PersonalizedRouteRecipe[]): PersonalizedShoppingSection[] {
  const sections = new Map(SHOPPING_SECTIONS.map((section) => [section.id, new Map<string, PersonalizedShoppingItem>()]));
  for (const recipe of recipes) {
    for (const ingredient of recipe.ingredients) {
      const name = shoppingItemName(ingredient);
      const section = shoppingSection(`${name} ${ingredient.display}`);
      const key = normalize(name);
      if (!key) continue;
      const items = sections.get(section)!;
      const existing = items.get(key);
      const quantity = recipe.servingMultiplier === 1
        ? ingredient.display
        : `${multiplierLabel(recipe.servingMultiplier)} ${ingredient.display}`;
      if (existing) {
        if (!existing.quantities.includes(quantity)) existing.quantities.push(quantity);
        if (!existing.recipeIds.includes(recipe.id)) existing.recipeIds.push(recipe.id);
      } else {
        items.set(key, {
          id: `${section}-${key.replaceAll(" ", "-").slice(0, 80)}`,
          name,
          quantities: [quantity],
          recipeIds: [recipe.id],
        });
      }
    }
  }
  return SHOPPING_SECTIONS.map((section) => ({
    ...section,
    items: [...sections.get(section.id)!.values()].sort((left, right) => left.name.localeCompare(right.name, "pt-BR")),
  })).filter((section) => section.items.length > 0);
}

export function buildPersonalizedRecipeRoute(
  catalog: RouteRecipeCatalog,
  answers: NormalizedRouteAnswers,
  profileId: ProfileId,
): PersonalizedRecipeRoute {
  if (catalog.schemaVersion !== 1 || catalog.recipes.length < 7) throw new Error("invalid_recipe_catalog");
  const home = household(answers);
  const style = preparationStyle(answers);
  const selected = selectRecipes(catalog, answers);
  const recipes = selected.map(({ recipe, reasons }) => {
    const adjustment = servingAdjustment(recipe, home.people);
    return {
      ...recipe,
      imageUrl: recipeImageUrl(recipe.image),
      why: `Entrou na sua rota porque ${reasons.slice(0, 3).join(", ") || "combina com as preferências informadas"}.`,
      servingMultiplier: adjustment.multiplier,
      servingNote: adjustment.note,
    };
  });
  const selectedMeal = firstValue(answers, "q2", "sua rotina").replaceAll("_", " ");
  const mealLabels: Record<string, string> = {
    cafe_da_manha: "Café da manhã",
    almoco: "Almoço",
    jantar: "Jantar",
    lanches: "Lanche",
    fim_de_semana: "Fim de semana",
  };
  const selectedMealLabel = mealLabels[firstValue(answers, "q2")] ?? selectedMeal;
  return {
    schemaVersion: 1,
    catalogDigest: catalog.catalogDigest,
    catalogRecipeCount: catalog.culinaryRecipeCount,
    profileId,
    headline: `7 receitas do catálogo CONAN selecionadas para ${selectedMealLabel.toLocaleLowerCase("pt-BR")}.`,
    preparationStyle: style,
    equipment: equipmentLabel(answers),
    household: home,
    shoppingPlan: shoppingPlan(answers),
    recipes,
    weeklyPlan: recipes.map((recipe, index) => ({
      day: index + 1,
      dayLabel: DAY_LABELS[index] ?? `Dia ${index + 1}`,
      recipeId: recipe.id,
      recipeTitle: recipe.title,
      mealLabel: selectedMealLabel,
      prepNote: planNote(style.id, recipe, index),
    })),
    shoppingList: shoppingList(recipes),
  };
}

export function isRouteRecipeCatalog(value: unknown): value is RouteRecipeCatalog {
  if (!value || typeof value !== "object") return false;
  const catalog = value as Partial<RouteRecipeCatalog>;
  return catalog.schemaVersion === 1 && typeof catalog.catalogDigest === "string" &&
    typeof catalog.culinaryRecipeCount === "number" && Array.isArray(catalog.recipes) && catalog.recipes.length >= 7;
}

export function isPersonalizedRecipeRoute(value: unknown): value is PersonalizedRecipeRoute {
  if (!value || typeof value !== "object") return false;
  const route = value as Partial<PersonalizedRecipeRoute>;
  return route.schemaVersion === 1 && typeof route.catalogDigest === "string" &&
    typeof route.catalogRecipeCount === "number" && typeof route.profileId === "string" &&
    typeof route.headline === "string" && Array.isArray(route.recipes) && route.recipes.length === 7 &&
    Array.isArray(route.weeklyPlan) && route.weeklyPlan.length === 7 && Array.isArray(route.shoppingList);
}
